import React, { useContext, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import { SimulationContext, SimulationContextProps } from "src/pages/coaching/components/simulation/view/context";

import { fetchPublicSimulation } from "src/redux/actions/coaching/simulation";
import { completeCourseContentSession, completeSimulation, enrollSimulation, fetchCourseContentSession, fetchLearningRecordSimulation, fetchSimulationReport, fetchTranscriptsForLearningSimulation, invalidateCompleteSimulation, invalidateCourseContentSession, invalidateSimulationReport, restartSimulation, restartSimulationLessonSession, retakeSimulation, startSimulation } from "src/redux/actions/coaching/learners";

import { SimulationSummaryComponent } from "src/pages/coaching/components/simulation/view";
import EmptyMessage from "src/components/emptyMessage";

import { LearningRecordSimulation, SimulationAssessmentReport } from "src/types/coaching/learners";
import { LearningRecordCourse } from "src/types/coaching/learners";

import _ from "lodash";
import styles from "./simulation.module.scss";
import { CourseContext } from "../../../context";
import { SimulationLesson } from "src/types/coaching/courses";

interface ViewSimulationContentProps {
  course: LearningRecordCourse;
  content: SimulationLesson;
  onSimulatorModeChange?: (isSimulator: boolean) => void;
}

export const ViewSimulationContent = ({
  course,
  content,
  onSimulatorModeChange,
}: ViewSimulationContentProps) => {
  const courseContext = useContext(CourseContext);
  const dispatch = useDispatch();

  const id = _.get(content, "content.id");

  const [simulationContext, setSimulationContext] = React.useState<SimulationContextProps>({
    id,
    view: "summary",
  });

  const [simulationState, setSimulationState] = useState({
    learningRecord: null,
    simulation: null,
  });

  const { creating, fetching, childLearningRecord, session, status, report, restartingSimulation, restartedSimulation, completingSimulation, completedSimulation } = useMemo(() => {
    const item = courseContext?.courseContentSession;

    const creating = _.get(item, "creating") || false;
    const fetching = _.get(item, "fetching") || false;

    const childLearningRecord = _.get(item, "childLearningRecord");
    const session = _.get(item, "session");
    const status = _.get(item, "status");
    const report = _.get(item, "report") as unknown as SimulationAssessmentReport;

    const restartingSimulation = _.get(item, "restarting") || false;
    const restartedSimulation = _.get(item, "restarted") || false;

    const completingSimulation = _.get(item, "completing") || false;
    const completedSimulation = _.get(item, "completed") || false;

    return {
      creating,
      fetching,
      childLearningRecord,
      session,
      status,
      report,
      completingSimulation,
      completedSimulation,
      restartingSimulation,
      restartedSimulation,
    }
  }, [courseContext?.courseContentSession]);

  const learningRecordSimulation = useSelector((state: any) => {
    if (childLearningRecord) {
      return _.get(state.learners, `simulation[${childLearningRecord}].item`);
    }

    return null;
  });

  const publicSimulation = useSelector((state: any) => {
    return _.get(state.coaching, `simulations.simulation[${id}].item`);
  });

  const simulation = learningRecordSimulation || publicSimulation;

  const loadingPublicSimulation = useSelector((state: any) =>
    _.get(state.coaching, `simulations.simulation[${id}].loading`)
  );

  const learnerSimulation = useSelector((state: any) =>
    _.get(state.learners, `simulation[${childLearningRecord}].item`)
  );

  const loadingLearnerSimulation = useSelector((state: any) =>
    _.get(state.learners, `simulation[${childLearningRecord}].loading`)
  );

  const fetchingSimulationReport = useSelector((state: any) => {
    const fetching = _.get(state.learners, `simulation[${childLearningRecord}].report.fetching`);
    return fetching;
  });

  const fetchedSimulationReport = useSelector((state: any) => {
    const fetched = _.get(state.learners, `simulation[${childLearningRecord}].report.fetched`);
    return fetched;
  });

  const transcripts = useSelector((state: any) => {
    return _.get(state.learners, `simulation[${childLearningRecord}].transcripts.item`);
  });

  const fetchingSimulationTranscripts = useSelector((state: any) => {
    const fetching = _.get(state.learners, `simulation[${childLearningRecord}].transcripts.fetching`);
    return fetching;
  });

  const fetchedSimulationTranscripts = useSelector((state: any) => {
    const fetched = _.get(state.learners, `simulation[${childLearningRecord}].transcripts.fetched`);
    return fetched;
  });

  const handleChangeView = (view: any) => {
    setSimulationContext((prev) => {
      return {
        ...prev,
        view: view,
        secondaryView: null,
      }
    });
  }

  const handleSecondaryChangeView = (view: any) => {
    setSimulationContext((prev) => {
      return {
        ...prev,
        secondaryView: view,
      }
    });
  }

  const handleEnrollSimulation = React.useCallback(() => {
    if (courseContext.createCourseContentSession) {
      courseContext.createCourseContentSession(content);
    }
  }, [content]);

  const handleStartSimulation = () => {
    if (childLearningRecord) {
      dispatch(restartSimulationLessonSession({
        course: course.course.id!,
        learningRecord: course.id!,
        type: "simulation",
        session: session!,
      }));
    }
  };

  const handleRestartSimulation = () => {
    if (childLearningRecord) {
      dispatch(restartSimulationLessonSession({
        course: course.course.id!,
        learningRecord: course.id!,
        type: "simulation",
        session: session!,
      }));
    }
  };

  const handleRetakeSimulation = () => {
    if (childLearningRecord) {
      dispatch(restartSimulationLessonSession({
        course: course.course.id!,
        learningRecord: course.id!,
        type: "simulation",
        session: session!,
      }));
    }
  };

  const handleEndCourse = (simulation: LearningRecordSimulation) => {
    dispatch(completeCourseContentSession({
      course: course.course.id!,
      learningRecord: course.id!,
      id: content.id!,
      type: "simulation",
      session: session!,
    }));
  };

  const handleViewReport = () => {
    if (!report) {
      dispatch(fetchSimulationReport({
        id: learnerSimulation.id!,
      }));
    } else {
      handleChangeView("report");
    }
  }

  const handleFetchTranscripts = () => {
    dispatch(fetchTranscriptsForLearningSimulation(learnerSimulation.id!));
  }

  useEffect(() => {
    if (childLearningRecord && !learningRecordSimulation) {
      dispatch(fetchLearningRecordSimulation(childLearningRecord));
    } else if (id && !publicSimulation) {
      dispatch(fetchPublicSimulation({ id: id }));
    }
  }, [childLearningRecord, id]);

  useEffect(() => {
    if (!childLearningRecord && simulation) {
      setSimulationContext((prev) => {
        return {
          ...prev,
          view: learnerSimulation?.status === 'IN_PROGRESS' ? "simulator" : "summary",
          restartingSimulation: restartingSimulation,
          retakingSimulation: restartingSimulation,
          enrollSimulation: handleEnrollSimulation,
          startSimulation: handleStartSimulation,
          restartSimulation: handleRestartSimulation,
          retakeSimulation: handleRetakeSimulation,
          secondaryView: null,
          endSimulation: handleEndCourse,
          viewReport: handleViewReport,
          fetchTranscripts: handleFetchTranscripts,
          changeView: handleChangeView,
          changeSecondaryView: handleSecondaryChangeView,
        };
      });

      setSimulationState((prev) => {
        return {
          ...prev,
          simulation: {
            ...simulation,
            id: simulation.id!,
            simulation: simulation,
            session: null,
            status: "NOT_ENROLLED",
          },
        };
      });
    }
  }, [childLearningRecord, simulation?.id]);

  useEffect(() => {
    if (childLearningRecord && learningRecordSimulation) {
      const ended = _.get(courseContext?.courseContentSession, 'ended');
      const completed = _.get(courseContext?.courseContentSession, 'completed');
      const report = learningRecordSimulation.report;
      const reportStatus = report?.status;
      let view = simulationContext.view;

      if (ended && completed && (!report || reportStatus === "in_sufficient_duration")) { 
        view = "failed";
      }

      setSimulationContext((prev) => {
        return {
          ...prev,
          view: view,
          restartingSimulation: restartingSimulation,
          retakingSimulation:restartingSimulation,
          enrollSimulation: handleEnrollSimulation,
          startSimulation: handleStartSimulation,
          restartSimulation: handleRestartSimulation,
          retakeSimulation: handleRetakeSimulation,
          secondaryView: null,
          endSimulation: handleEndCourse,
          viewReport: handleViewReport,
          fetchTranscripts: handleFetchTranscripts,
          changeView: handleChangeView,
          changeSecondaryView: handleSecondaryChangeView,
        };
      });

      setSimulationState((prev) => {
        return {
          ...prev,
          simulation: learningRecordSimulation
        };
      });
    }
  }, [childLearningRecord, learningRecordSimulation]);

  useEffect(() => {
    if (childLearningRecord && learningRecordSimulation) {
      dispatch(fetchCourseContentSession({
        course: course.course!.id!,
        learningRecord: course.id!,
        id: content.id,
        type: "simulation",
        session: session!,
      }));

      setSimulationState((prev) => {
        return {
          ...prev,
          simulation: learningRecordSimulation
        };
      });
    }
  }, [childLearningRecord, learningRecordSimulation?.id, learningRecordSimulation?.session]);

  useEffect(() => {
    setSimulationContext((prev) => {
      return {
        ...prev,
        enrollingSimulation: creating,
        startingSimulation: creating,
      }
    });
  }, [creating]);

  useEffect(() => {
    if (restartedSimulation) {
      setSimulationContext((prev) => ({
        ...prev,
        view: "simulator",
      }));

      dispatch(invalidateCourseContentSession({
        course: course.course!.id!,
        learningRecord: course.id!,
        id: content.id,
        type: "simulation",
      }));
    }
  }, [restartedSimulation]);

  useEffect(() => {
    setSimulationContext((prev) => {
      return {
        ...prev,
        endingSimulation: completingSimulation
      }
    });
  }, [completingSimulation]);

  useEffect(() => {
    const ended = _.get(courseContext?.courseContentSession, 'ended');
    const completed = _.get(courseContext?.courseContentSession, 'completed');
    if (ended && completed) {
      console.log("simulation session", courseContext?.courseContentSession);
      const reportStatus = _.get(courseContext?.courseContentSession, "report.status");
      if (!simulation.report || reportStatus === "in_sufficient_duration") {
        handleChangeView("failed");
      } else {
        handleChangeView("completion");
      }
    }
  }, [courseContext?.courseContentSession]);

  useEffect(() => {
    setSimulationContext((prev) => {
      return {
        ...prev,
        fetchingReport: fetchingSimulationReport,
        fetchedReport: fetchedSimulationReport,
        assessmentReport: report,
      }
    });

    if (fetchedSimulationReport) {
      handleChangeView("report");
    }

    if (childLearningRecord) {
      dispatch(invalidateSimulationReport(childLearningRecord));
    }

  }, [fetchingSimulationReport, fetchedSimulationReport, report]);

  useEffect(() => {
    setSimulationContext((prev) => {
      return {
        ...prev,
        fetchingTranscripts: fetchingSimulationTranscripts,
        fetchedTranscripts: fetchedSimulationTranscripts,
        transcripts: transcripts
      }
    });
  }, [fetchingSimulationTranscripts, fetchedSimulationTranscripts, transcripts]);

  useEffect(() => {
    setSimulationContext((prev) => {
      return {
        ...prev,
        retakingSimulation: restartingSimulation,
        restartingSimulation: restartingSimulation,
      }
    });
  }, [restartingSimulation]);


  useEffect(() => {
    const fullScreenViews = ["simulator", "report"];
    const isFullScreen = fullScreenViews.includes(simulationContext.view);

    onSimulatorModeChange?.(isFullScreen);
  }, [simulationContext.view]);

  const loading = loadingPublicSimulation || loadingLearnerSimulation;

  if (!loading && !simulation) {
    return (
      <div className={styles.notFoundContainer}>
        <EmptyMessage
          width={500}
          image="/images/assets/404error_message.svg"
          heading="Mmm... who moved my cheese?"
          message="Sorry! Someone passed you the wrong information. You were never supposed to see me :). This podcast does not exist any longer."
        />
      </div>
    );
  }

  if (loading || !simulation) {
    return (
      <div className={styles.loading}>
        <img src="/images/loading_plane.gif" />
        <div className={styles.message}>Loading simulation...</div>
      </div>
    );
  }

  return (
    <SimulationContext.Provider value={simulationContext}>
      {simulationState.simulation &&
        <SimulationSummaryComponent
          simulation={simulationState.simulation}
          parentCourse={course}
          className={styles.simulationView}
          isReadOnly={course.status == 'COMPLETED'}
        />
      }
    </SimulationContext.Provider>
  );
};
